import React from "react";
import { useEditor } from "./store.js";
import { lookFilter } from "@hudhud/reel";
import type { CutStyle, EffectKey, LookPreset } from "./fxCatalog.js";

/** Two frames for a preview to hand over between, as CSS backgrounds. */
export interface Stills { a: string; b: string; /** b is a's still again (shown darker) because the clip has only one still yet */ bAlt: boolean }
const GRADIENT_A = "linear-gradient(135deg,#5f8f72,#1c3a28)";
const GRADIENT_B = "linear-gradient(135deg,#caa25a,#5a3d1a)";
const asBg = (url: string) => `url("${url}") center / cover no-repeat`;

/** The clip's own stills (the cover choices, then the poster); two gradients that read as different shots until the studio has made them. */
export const useStills = (): Stills => {
  const files = useEditor((s) => s.files);
  const urls = [...new Set([...(files?.covers ?? []).map((c) => c.url), ...(files?.poster ? [files.poster] : [])])];
  if (!urls.length) return { a: GRADIENT_A, b: GRADIENT_B, bAlt: false };
  // the first and last cover are the farthest apart in time, so the handover reads as two different moments
  return { a: asBg(urls[0]), b: asBg(urls[urls.length - 1]), bAlt: urls.length === 1 };
};

/** Two frames handing over with one cut style, on a 2.4 s loop drawn entirely in CSS (see "the transitions gallery" in theme.css). */
export const TransitionPreview: React.FC<{ style: CutStyle; stills: Stills; height: number }> = ({ style, stills, height }) => (
  <div className={`fxstage fx-${style}`} style={{ height }} aria-hidden="true">
    <div className="fxframe fxa" style={{ background: stills.a }} />
    <div className={`fxframe fxb${stills.bAlt ? " alt" : ""}`} style={{ background: stills.b }} />
    <div className="fxframe fxflash" />
  </div>
);

/** The still under one grade: the graded copy sits over the plain one at the look's strength, and the vignette is the clip's own. */
export const LookPreview: React.FC<{ preset: LookPreset; strength: number; vignette: number; still: string; height: number }> = ({ preset, strength, vignette, still, height }) => {
  const filter = lookFilter(preset, strength); // the render's own grade, so the tile, the preview and the file agree
  return (
    <div className="fxstage fx-look" style={{ height }} aria-hidden="true">
      <div className="fxframe fxdrift" style={{ background: still, filter: filter === "none" ? undefined : filter }} />
      <div className="fxframe fxvig" style={{ opacity: Math.min(1, vignette) }} />
    </div>
  );
};

/** One effect playing over the still; each is a CSS animation keyed off the stage's class (see "the effects gallery" in theme.css). */
export const EffectPreview: React.FC<{ effect: EffectKey; still: string; height: number }> = ({ effect, still, height }) => (
  <div className={`fxstage fxe-${effect}`} style={{ height }} aria-hidden="true">
    <div className="fxframe fxl-still" style={{ background: still }} />
    {effect === "glitch" ? <><div className="fxframe fxl-r" style={{ background: still }} /><div className="fxframe fxl-c" style={{ background: still }} /></> : null}
    {effect === "grain" ? <div className="fxl-grain" /> : null}
    {effect === "bars" ? <><i className="fxl-bar top" /><i className="fxl-bar bottom" /></> : null}
    {effect === "flash" ? <div className="fxframe fxflash on" /> : null}
    {effect === "lightLeak" ? <div className="fxl-leak" /> : null}
    {effect === "flicker" ? <div className="fxframe fxl-flicker" /> : null}
    {effect === "sparkle" ? <div className="fxl-sparkle" /> : null}
    {effect === "ring" ? <div className="fxl-ring" /> : null}
  </div>
);
